import React from "react";
import Reveal from "./Reveal";
import {
  Wrench, Droplets, Waves, Building2, Factory, Home, Cog, ShieldCheck, GitBranch, Hammer,
} from "lucide-react";

const services = [
  { icon: Wrench, title: "Sanitary Engineering", body: "End-to-end design and execution of sanitary systems engineered to last." },
  { icon: GitBranch, title: "Plumbing Contracting", body: "Turnkey contracting for new builds, retrofits and complex extensions." },
  { icon: Waves, title: "Drainage Systems", body: "Gravity, vacuum and pumped drainage designed for code and capacity." },
  { icon: Droplets, title: "Water Supply Systems", body: "Pressurised distribution, hydropneumatic boosting and tank engineering." },
  { icon: Building2, title: "High-Rise Plumbing", body: "Multi-storey risers, pressure zoning and acoustic-aware stack design." },
  { icon: Cog, title: "Mivan-Compatible Solutions", body: "Precision sleeve, conduit and chase coordination for Mivan formwork." },
  { icon: Factory, title: "Industrial Plumbing", body: "Plant utilities, process water, effluent lines and compressed-air services." },
  { icon: Home, title: "Residential Systems", body: "Quiet, efficient plumbing engineered for premium homes and townships." },
  { icon: ShieldCheck, title: "Commercial Infrastructure", body: "Corporate, hospitality and retail plumbing built to operational standards." },
  { icon: Hammer, title: "Maintenance & Upgrades", body: "Audits, refurbishments and lifecycle upgrades for ageing infrastructure." },
];

export const Services = () => {
  return (
    <section id="services" data-testid="services-section" className="relative py-28 md:py-40 bg-ink-800">
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-12 gap-6 items-end mb-16">
          <div className="col-span-12 lg:col-span-7">
            <Reveal>
              <span className="font-mono text-[11px] uppercase tracking-[0.32em] text-gold">
                / 02 — Capabilities
              </span>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight">
                A full discipline of <span className="italic text-gold">water &amp; sanitation</span> engineering.
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-5 lg:pl-10">
            <Reveal delay={200}>
              <p className="text-base text-steel-light leading-relaxed">
                Every service we offer is delivered by our own teams, supervised by senior
                engineers, and held to the same standard whether the site is a thirty-storey
                tower or a single manufacturing shed.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-px bg-white/5 border border-white/5">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal
                key={s.title}
                delay={(i % 5) * 80}
                className="group relative bg-ink-900 p-8 hover:bg-ink-700 transition-colors duration-500"
              >
                <div
                  data-testid={`service-card-${i}`}
                  className="flex flex-col h-full"
                >
                  <div className="flex items-center justify-between">
                    <Icon
                      size={28}
                      strokeWidth={1.25}
                      className="text-gold transition-transform duration-500 group-hover:scale-110"
                    />
                    <span className="font-mono text-[10px] text-steel uppercase tracking-[0.26em]">
                      0{i + 1 < 10 ? i + 1 : i + 1}
                    </span>
                  </div>
                  <h3 className="mt-10 font-display text-2xl text-white leading-tight">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm text-steel-light leading-relaxed">
                    {s.body}
                  </p>
                  <div className="mt-6 h-px w-8 bg-gold/40 group-hover:w-20 group-hover:bg-gold transition-all duration-500" />
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
